import { Masonry } from "@mui/lab";
import { Box, Paper, styled } from "@mui/material";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSetRecoilState } from "recoil";
import { DialogMessageState, MoveButtonState } from "../../recoil/states";
import { asyncReqBoardInfo } from "../../reducers/BoardInfoReducer";
import { AppDispatch, RootState } from "../../Store";
import CommonUtil from "../../utils/CommonUtil";

const BoardJokeNextList = (props: { mode: "MUI" | "AJS" }) => {

    const BoardJokeNextListReducer_Result = useSelector((state: RootState) => state.BoardJokeNextListReducer); // 하단 Masonry 레이아웃 표시 처리를 위한 state
    const setDialogMessageState = useSetRecoilState(DialogMessageState); // 다일얼로그 메세지 요청 정보를 갖는 state
    const setMoveButtonState = useSetRecoilState(MoveButtonState); // '순차 / 랜덤' 버튼 클릭 여부 정보를 갖는 state
    const dispatch = useDispatch<AppDispatch>();

    /**
    * 게시물 이미지 Load 실패 이벤트
    * 
    * @param e 이벤트 객체
    * @param baseJpg 이미지 URL
    * @returns 
    */
    // 각 <img>마다 폴백 단계 관리: 0=avif 실패, 1=webp 실패, 2=jpg도 실패(끝)
    const handleImageError = (
        e: React.SyntheticEvent<HTMLImageElement>,
        baseJpg: string
    ) => {
        const img = e.currentTarget as HTMLImageElement;
        const pic = img.parentElement as HTMLPictureElement | null;
        const step = Number(img.dataset.fallbackStep || "0");

        const disableSource = (type: "image/avif" | "image/webp") => {
            if (!pic) return;
            const s = pic.querySelector<HTMLSourceElement>(`source[type="${type}"]`);
            if (s) {
                // 둘 중 하나만 해도 충분합니다. 둘 다 해두면 더 확실.
                s.removeAttribute("srcset");     // 후보 제거
                s.setAttribute("media", "not all"); // 선택 대상 제외
            }
        };

        if (step === 0) {
            // avif 실패 → avif 후보 제거 후 webp로 시도
            disableSource("image/avif");
            img.dataset.fallbackStep = "1";
            img.src = baseJpg.replace(".jpg", ".webp");
            return;
        }

        if (step === 1) {
            // webp 실패 → webp 후보 제거 후 jpg로 시도
            disableSource("image/webp");
            img.dataset.fallbackStep = "2";
            img.src = baseJpg;
            return;
        }

        // 마지막까지 실패하면 루프 방지
        img.onerror = null;
        // 필요하면 플레이스홀더로 교체
        // img.src = "/images/no-image.png";
    };

    // 다음 게시물 목록 비동기 통신 중, 발생한 오류 메세지 표시
    if (BoardJokeNextListReducer_Result.status === "COMPLETE") {
        if (BoardJokeNextListReducer_Result.n4ErrorCode !== 0) {
            const message = BoardJokeNextListReducer_Result.strErrorMessage;
            setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: false, message }));
        }
    }

    if (BoardJokeNextListReducer_Result.status === "LOADING") {
        return (
            <p id="p_joke_next_loading" className="bo_vc_empty">
                <img src="/images/ajax-loader-2.gif" alt="" /><br />게시물 목록을 가져오는 중입니다..
            </p>
        );
    }
    else {
        if (BoardJokeNextListReducer_Result.LIST.length === 0) {
            return (
                <p id="p_joke_next_loading" className="bo_vc_empty">
                    표시할 다음 게시물이 없습니다.
                </p>
            );
        }
        else {
            if (props.mode === "MUI") {
                // 'MUI' 방식 요청 일 경우

                /**
                * 이동 버튼 버튼 클릭 이벤트
                * 
                * @param {React.MouseEvent<HTMLAnchorElement, MouseEvent>} e 이벤트 객체
                * @param {string} board_seq 게시판 고유번호
                * @returns 
                */
                const handlerAnchorLink = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, board_seq: string) => {
                    e.preventDefault();

                    // '랜덤' 버튼 클릭 알림 (마지막으로 본 고유번호 갱신되지 않도록 하기 위함)
                    setMoveButtonState(rawState => ({ ...rawState, isClickBtnOrder: false, isClickBtnRandom: true }));

                    if (navigator.userAgent.indexOf(";UGGIYO_APP;") > -1) {
                        // 앱 내 호출일 경우
                        const AppWebView = (window as any).AppWebView;
                        if (typeof (AppWebView) !== "undefined") {
                            if (typeof (AppWebView.show_New_Comment_Detail) === "function") {

                                AppWebView.show_New_Comment_Detail(board_seq, "");
                            }
                        }
                    }
                    else {
                        // 그 외
                        const url: string = "/board_info?seq=" + encodeURIComponent(board_seq);
                        (window as any).urlChange(url);

                        dispatch(asyncReqBoardInfo({ view_type: "CURR", board_seq })); // 게시판 상세정보 가져오기

                        CommonUtil.scrollTo("header", -7, 0); // 상단 메뉴 영역 안보이게 스크롤바 자동 이동
                    }
                }

                const Label = styled(Paper)(({ theme }) => ({
                    padding: theme.spacing(2),
                    textAlign: 'center',
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                    borderStyle: "solid",
                    borderColor: "#d1d1d1",
                    borderWidth: "0px 1px 0px 1px",
                    fontSize: 12,
                }));

                return (
                    <Masonry columns={2} spacing={1}>
                        {BoardJokeNextListReducer_Result.LIST.map((item, index) => (
                            <a href="#none" onClick={e => handlerAnchorLink(e, item.UBM_BOARD_SEQ)} key={index + "_" + item.UBM_BOARD_SEQ} style={{ textDecorationLine: "none" }}>
                                <div style={{ maxHeight: 600, overflowY: "hidden" }}>
                                    <picture>
                                        <source type="image/avif" srcSet={item.V_PREVIEW_IMG_PATH.replace(".jpg", ".avif")} />
                                        <source type="image/webp" srcSet={item.V_PREVIEW_IMG_PATH.replace(".jpg", ".webp")} />
                                        <img
                                            className="figure__image"
                                            src={item.V_PREVIEW_IMG_PATH}
                                            alt={item.UBM_TITLE}
                                            loading="lazy"
                                            data-fallback-step="0" // ← 폴백 단계 초기화
                                            style={{
                                                borderTopLeftRadius: 4,
                                                borderTopRightRadius: 4,
                                                display: 'block',
                                                width: '100%',
                                                border: "1px solid #d1d1d1",
                                                padding: 3
                                            }}
                                            onError={(e) => handleImageError(e, item.V_PREVIEW_IMG_PATH)}
                                        />
                                    </picture>
                                </div>
                                <Label>{item.UBM_IMG_PATH.indexOf(".gif") > -1 && <><img src="/images/icon_gif.png" alt="" />&nbsp;</>}{item.UBM_IMG_PATH.indexOf(".mp4") > -1 && <><img src="/images/icon_mp4.png" alt="" />&nbsp;</>}{item.UBM_TITLE}</Label>
                            </a>
                        ))}
                    </Masonry>
                )
            }
            else if (props.mode === "AJS") {
                // '앵귤러 JS' 방식 요청 일 경우

                return (
                    <Box>
                        <object id="uggiyo_masonry_web_root" data={"/angulargrid.html?key=" + Math.random()} title="masonry layout" style={{ border: 0, width: "100%", height: 0, overflow: "hidden" }}></object>
                        <input type="hidden" id="hid_BoardJokeNextList_JsonData" value={JSON.stringify(BoardJokeNextListReducer_Result.LIST)} />
                    </Box>
                );
            }
            else {
                // 그 외

                return <></>;
            }
        }
    }
}

export default BoardJokeNextList;
